/**
 * Offline packs manager: loads the stored state, syncs against the authoritative manifest (prunes
 * everything no longer visible), saves/removes/re-downloads packs and keeps cached avatars current.
 * The download queue itself lives in downloader.ts; the viewer/audio hooks in sources.ts.
 */
import { roleAtLeast } from '../../shared/audience';
import type { MeDTO } from '../../shared/dto/auth';
import type { CollectionDTO, SongDTO, SongListItemDTO } from '../../shared/dto/library';
import type { MemberDTO } from '../../shared/dto/members';
import { avatarUrl, type ManifestDTO } from '../../shared/dto/offline';
import { api, apiRaw, isNetworkFailure } from '../api/client';
import { fetchSong, SONGS_KEY, songKey } from '../api/library';
import { queryClient } from '../api/queryClient';
import { closeOfflineDb, deleteFromCache, getMeta, offlineDb, offlineOp, openContent, openRecent, setMeta } from './db';
import { cachePath, contentStrays, diffManifest, manifestIndex, plannedUrls, pruneRecent, recentStrays, removedSheetIds, staleFileUrls } from './diff';
import { abortCurrentDownload, abortUnwantedDownload, busyUrl, nudgeQueue, roomFor, runQueue, setDownloaderHooks } from './downloader';
import { dropRecentUrls } from './lru';
import { deviceTier, packSongIds, planPack, plannedBytes } from './plan';
import { currentPlans, data, isCurrent, missingItems, publish, resetData } from './store';
import { packId, type ItemRow, type PackKind, type PackRow, type RecentEntry } from './types';
import { validateAvatarResponse } from './validate';
import { withReopen } from '../lib/idb';

/** Thrown by savePack(): 'offline' (no manifest can be fetched), 'gone' (song/collection not visible), 'storage' (IndexedDB unavailable). */
export class SavePackError extends Error {
  constructor(public readonly code: 'offline' | 'gone' | 'storage') {
    super(code);
  }
}

let loading: Promise<void> | null = null;
/** Whether the stored state of the bound user could be read ('none' = no user bound). */
let loadState: 'none' | 'ok' | 'failed' = 'none';
let lastSyncOkAt = 0;

/* ─── User binding & loading ────────────────────────────────────────────── */

function currentTier(): 'm' | 'l' {
  if (typeof window === 'undefined' || !window.screen) return 'l';
  return deviceTier(window.screen.width, window.screen.height, window.devicePixelRatio || 1);
}

async function load(gen: number): Promise<void> {
  try {
    const db = offlineDb();
    // A connection Safari dropped while the app was suspended is reopened once (lib/idb.ts).
    const [packs, items, manifest, recent, tier] = await withReopen(db, () =>
      Promise.all([db.packs.toArray(), db.items.toArray(), getMeta('manifest'), getMeta('recent'), getMeta('tier')]),
    );
    if (!isCurrent(gen)) return;
    data.packs = new Map(packs.map((p) => [p.id, p]));
    data.items = new Map(items.map((i) => [i.url, i]));
    data.manifest = manifest && manifest.userId === data.user?.id ? manifest : null;
    data.recent = recent ?? [];
    data.tier = currentTier();
    loadState = 'ok';
    if (tier !== data.tier) await setMeta('tier', data.tier);
  } catch (e) {
    if (isCurrent(gen) && loadState !== 'ok') loadState = 'failed';
    console.warn('offline state unavailable', e);
  } finally {
    if (isCurrent(gen)) {
      data.loaded = true;
      publish();
    }
  }
}

/**
 * Resolves once the bound user's stored state was read: 'ok', 'failed' (IndexedDB unreadable — what
 * is cached cannot be checked against the items table) or 'none' (no user bound).
 */
export async function offlineStateLoaded(): Promise<'none' | 'ok' | 'failed'> {
  await loading;
  return data.user ? loadState : 'none';
}

/** Ends the in-memory session: work of the old generation stops writing; per-session runs restart. */
function resetSession(): void {
  abortCurrentDownload();
  resetData();
  loading = null;
  loadState = 'none';
  lastSyncOkAt = 0;
  refreshQueue.clear();
}

/** Binds the signed-in user (planning uses role + section). A different user starts from scratch. */
export function bindUser(me: Pick<MeDTO, 'id' | 'role' | 'section'>): Promise<void> {
  if (data.user && data.user.id === me.id) {
    if (data.user.role !== me.role || data.user.section !== me.section) {
      data.user = { id: me.id, role: me.role, section: me.section };
      publish();
    }
    return loading ?? Promise.resolve();
  }
  if (data.user) resetSession();
  data.user = { id: me.id, role: me.role, section: me.section };
  loading = load(data.generation);
  return loading;
}

/** Stops downloads and forgets the in-memory state, then closes the DB (before a wipe). */
export function stopOfflineRuntime(): void {
  resetSession();
  closeOfflineDb();
}

/* ─── Recent LRU list (the only writer of data.recent + meta 'recent') ──── */

let recentChain: Promise<unknown> = Promise.resolve();

/**
 * Serialises every read-modify-write of the recent-songs list (opened songs, manifest pruning,
 * evictions, "Hreinsa"): each step runs against the then-current list, so no change is lost and the
 * list never forgets a copy that stays in 'kgr-recent-v1'. `fn` returns the new list (null = unchanged).
 */
export function withRecent(fn: (list: readonly RecentEntry[]) => Promise<RecentEntry[] | null>): Promise<void> {
  const gen = data.generation;
  const next = recentChain.then(async () => {
    await loading;
    if (!isCurrent(gen)) return;
    const updated = await fn(data.recent);
    if (!updated || !isCurrent(gen)) return;
    data.recent = updated;
    try {
      await offlineOp((db) => db.meta.put({ k: 'recent', v: updated }));
    } catch {
      /* the in-memory list still bounds the cache for this session */
    }
    publish();
  });
  recentChain = next.catch(() => undefined);
  return next;
}

/* ─── Manifest sync ─────────────────────────────────────────────────────── */

/** The manifest sync in flight, tagged with its session (a new user never waits for the old one's). */
let syncing: { gen: number; p: Promise<boolean> } | null = null;

/** Fetches the manifest and prunes / re-plans against it. Resolves false when it could not be fetched. */
export function syncManifest(): Promise<boolean> {
  if (syncing && syncing.gen === data.generation) return syncing.p;
  const p: Promise<boolean> = doSync().finally(() => {
    if (syncing?.p === p) syncing = null;
  });
  syncing = { gen: data.generation, p };
  return p;
}

async function doSync(): Promise<boolean> {
  const gen = data.generation;
  const user = data.user;
  if (!user) return false;
  await loading;
  if (!isCurrent(gen)) return false;
  data.syncing = true;
  publish();
  try {
    const manifest = await api<ManifestDTO>('/offline/manifest', { timeoutMs: 30_000 });
    if (!isCurrent(gen) || manifest.userId !== user.id) return false;
    await applyManifest(gen, manifest);
    if (isCurrent(gen)) {
      data.syncFailed = false;
      lastSyncOkAt = Date.now();
    }
    return true;
  } catch (e) {
    if (isCurrent(gen)) data.syncFailed = true;
    if (!isNetworkFailure(e)) console.warn('offline manifest sync failed', e);
    return false;
  } finally {
    if (isCurrent(gen)) {
      data.syncing = false;
      publish();
    }
  }
}

/**
 * Annotation layers of sheets that are no longer visible are removed from the device as well:
 *  1. the sheets the manifest diffs listed (meta 'pendingPurge', stored with the manifest that
 *     dropped them) — cleared only once the purge succeeded, so an interrupted one is retried. A
 *     listed sheet this manifest shows again (audience or section changed back before the purge
 *     ran) is not purged: its layers — and the user's unsent edits on it — stay;
 *  2. every other stored sheet this manifest does not list (opened between syncs, a lost purge, a
 *     manifest that could not be read) — reconciled on every successful sync.
 */
export async function purgeAnnotations(gen: number, manifest: ManifestDTO, pending: readonly string[]): Promise<void> {
  try {
    const m = await import('../annotations/local');
    const sheets = manifest.files.filter((f) => f.kind === 'sheet').map((f) => f.id);
    if (pending.length > 0) {
      const visible = new Set(sheets);
      const purge = pending.filter((id) => !visible.has(id));
      const done = purge.length === 0 || (await m.purgeSheetAnnotations(purge));
      // Every listed sheet is settled now: purged, or visible again.
      if (done && isCurrent(gen)) await clearPendingPurge(pending);
    }
    if (!isCurrent(gen)) return;
    await m.purgeAnnotationsNotIn(manifest.userId, sheets);
  } catch (e) {
    console.warn('annotation purge failed', e);
  }
}

/** Removes purged sheets from meta 'pendingPurge' (others added meanwhile stay). */
async function clearPendingPurge(done: readonly string[]): Promise<void> {
  const db = offlineDb();
  const purged = new Set(done);
  await db.transaction('rw', db.meta, async () => {
    const cur = ((await db.meta.get('pendingPurge'))?.v as string[] | undefined) ?? [];
    const rest = cur.filter((id) => !purged.has(id));
    if (rest.length > 0) await db.meta.put({ k: 'pendingPurge', v: rest });
    else await db.meta.delete('pendingPurge');
  });
}

async function applyManifest(gen: number, manifest: ManifestDTO): Promise<void> {
  const gone = removedSheetIds(data.manifest, manifest);
  data.manifest = manifest;
  const plans = currentPlans();
  const diff = diffManifest({ manifest, items: [...data.items.values()], packs: [...data.packs.values()], plans, recent: data.recent });

  // Deletions first: nothing that is no longer visible may stay on the device.
  const [content, recentCache] = await Promise.all([openContent(), openRecent()]);
  await deleteFromCache(content, diff.deleteUrls);
  if (!isCurrent(gen)) return;
  // The recent list is pruned against the list as it is when this step runs (songs opened meanwhile count).
  const idx = manifestIndex(manifest);
  await withRecent(async (list) => {
    const pruned = pruneRecent(list, idx, plannedUrls(currentPlans()));
    await deleteFromCache(recentCache, pruned.deleteUrls);
    return pruned.changed ? pruned.recent : null;
  });
  if (!isCurrent(gen)) return;

  const db = offlineDb();
  const nextPacks: PackRow[] = [];
  for (const [id, out] of diff.packs) {
    const p = data.packs.get(id);
    if (p) nextPacks.push({ ...p, status: out.status, error: out.error, bytes: out.bytes });
  }
  // A pack removed by the user while this sync ran must not come back.
  const stillThere = () => nextPacks.filter((p) => data.packs.has(p.id));
  let pendingPurge: string[] = [];
  await db.transaction('rw', db.items, db.packs, db.meta, async () => {
    await db.items.bulkDelete(diff.deleteUrls);
    for (const [url, packIds] of diff.packIdUpdates) await db.items.update(url, { packIds });
    await db.packs.bulkDelete(diff.removePacks);
    await db.packs.bulkPut(stillThere());
    await db.meta.put({ k: 'manifest', v: manifest });
    // With the manifest that dropped them: the next diff will not list these sheets again.
    const earlier = ((await db.meta.get('pendingPurge'))?.v as string[] | undefined) ?? [];
    pendingPurge = [...new Set([...earlier, ...gone])];
    if (pendingPurge.length > 0) await db.meta.put({ k: 'pendingPurge', v: pendingPurge });
  });
  if (!isCurrent(gen)) return;
  void purgeAnnotations(gen, manifest, pendingPurge);

  for (const url of diff.deleteUrls) data.items.delete(url);
  for (const [url, packIds] of diff.packIdUpdates) {
    const it = data.items.get(url);
    if (it) data.items.set(url, { ...it, packIds });
  }
  for (const id of diff.removePacks) data.packs.delete(id);
  for (const p of stillThere()) data.packs.set(p.id, p);
  publish();
  // The statuses above were diffed before the awaits: the queue may have completed a pack (or an
  // eviction may have taken a file of a complete one) meanwhile. Settle them from the state now.
  await settlePackStatuses(gen);
  if (!isCurrent(gen)) return;

  await sweepCaches(gen, content, recentCache);
  if (!isCurrent(gen)) return;
  pruneQueryCache(manifest);
  void refreshSongDetails();
  void syncAvatars();
  if ([...data.packs.keys()].some((id) => missingItems(id).length > 0)) void runQueue();
}

/** Pack status from what is stored now: complete when nothing is missing, else a complete pack needs an update. */
export function settledPackStatus(cur: Pick<PackRow, 'status'>, missing: number): PackRow['status'] {
  if (missing === 0) return 'complete';
  return cur.status === 'complete' ? 'update' : cur.status;
}

async function settlePackStatuses(gen: number): Promise<void> {
  const changed: PackRow[] = [];
  for (const cur of [...data.packs.values()]) {
    const status = settledPackStatus(cur, missingItems(cur.id).length);
    const error = status === 'complete' ? null : cur.error;
    if (status === cur.status && error === cur.error) continue;
    const next = { ...cur, status, error };
    data.packs.set(cur.id, next);
    changed.push(next);
  }
  if (changed.length === 0) return;
  publish();
  try {
    const db = offlineDb();
    await db.transaction('rw', db.packs, async () => {
      // Only the status of packs that still exist (one removed meanwhile stays removed).
      for (const p of changed) if (isCurrent(gen) && data.packs.has(p.id)) await db.packs.update(p.id, { status: p.status, error: p.error });
    });
  } catch {
    /* memory is right; the queue / next sync writes the status again */
  }
}

/**
 * Reconciliation of the caches with the bookkeeping:
 *  - copies nothing lists (a put whose items row never landed — quota, app killed — or an
 *    interrupted removal) are never served, LRU-evicted or pruned by a diff: deleted here;
 *  - items rows whose copy is gone (an interrupted re-download) are dropped, so their packs fetch
 *    the file again instead of showing it as saved.
 */
async function sweepCaches(gen: number, content: Cache | null, recentCache: Cache | null): Promise<void> {
  try {
    if (content) {
      // Rows recorded before the key listing must have their copy in it (a copy is stored before its row).
      const rowsBefore = [...data.items.keys()];
      const keys = (await content.keys()).map((r) => r.url);
      // Avatars stored while a sync of them runs are left for the next sweep.
      const avatars = avatarRun ? null : new Set((await getMeta('avatars').catch(() => undefined)) ?? []);
      if (!isCurrent(gen)) return;
      const busy = busyUrl();
      await deleteFromCache(content, contentStrays(keys, { items: data.items, avatars, busy }));
      const present = new Set(keys.map(cachePath));
      const dangling = rowsBefore.filter((u) => u !== busy && data.items.has(u) && !present.has(u));
      if (dangling.length > 0 && isCurrent(gen)) await evictUrls(dangling);
    }
    if (recentCache && isCurrent(gen)) {
      await withRecent(async (list) => {
        await deleteFromCache(recentCache, recentStrays((await recentCache.keys()).map((r) => r.url), list));
        return null;
      });
    }
  } catch (e) {
    console.warn('offline cache sweep failed', e);
  }
}

/**
 * Persisted query data must not keep what the user can no longer see either: collections that are
 * gone, and (members) songs / files that left the manifest. Editors see every song and pending file
 * in the library views, so their song caches are left alone.
 */
function pruneQueryCache(manifest: ManifestDTO): void {
  const cache = queryClient.getQueryCache();
  for (const q of cache.findAll({ queryKey: ['collection'] })) {
    const id = q.queryKey[1];
    if (typeof id === 'string' && id && !(id in manifest.collections)) queryClient.removeQueries({ queryKey: q.queryKey, exact: true });
  }
  if (!data.user || roleAtLeast(data.user.role, 'editor')) return;
  for (const q of cache.findAll({ queryKey: ['song'] })) {
    const id = q.queryKey[1];
    if (typeof id !== 'string' || !id) continue;
    const visible = manifest.songs[id];
    if (!visible) {
      queryClient.removeQueries({ queryKey: q.queryKey, exact: true });
      continue;
    }
    const song = q.state.data as SongDTO | undefined;
    if (song && song.files.some((f) => !visible.includes(f.id))) {
      queryClient.setQueryData<SongDTO>(songKey(id), { ...song, files: song.files.filter((f) => visible.includes(f.id)) });
    }
  }
  queryClient.setQueryData<CollectionDTO[]>(['collections'], (list) => list?.filter((c) => c.id in manifest.collections));
}

/** Song ids covered by any pack (current manifest). */
function packedSongIds(): Set<string> {
  const out = new Set<string>();
  if (!data.manifest) return out;
  for (const p of data.packs.values()) for (const id of packSongIds(p, data.manifest) ?? []) out.add(id);
  return out;
}

let refreshing: { gen: number; p: Promise<void> } | null = null;
const refreshQueue = new Set<string>();

/**
 * Song details (['song', id], persisted) are what the song screen and lyrics read offline: fetch
 * them for every saved song that has none cached or whose list entry says it changed.
 */
export function refreshSongDetails(only?: Iterable<string>): Promise<void> {
  for (const id of only ?? packedSongIds()) refreshQueue.add(id);
  if (refreshing) {
    // The loop of an earlier session stops at its next step: continue for this one afterwards.
    return refreshing.gen === data.generation ? refreshing.p : refreshing.p.then(() => refreshSongDetails([]));
  }
  const gen = data.generation;
  const p: Promise<void> = (async () => {
    while (refreshQueue.size > 0 && isCurrent(gen) && navigator.onLine) {
      const id = refreshQueue.values().next().value as string;
      refreshQueue.delete(id);
      const list = queryClient.getQueryData<SongListItemDTO[]>(SONGS_KEY);
      const stamp = list?.find((s) => s.id === id)?.updatedAt;
      const cached = queryClient.getQueryData<SongDTO>(songKey(id));
      if (cached && (stamp === undefined || cached.updatedAt === stamp)) continue;
      await queryClient
        .prefetchQuery({ queryKey: songKey(id), queryFn: ({ signal }) => fetchSong(id, signal), meta: { persist: true }, staleTime: 0, retry: 1 })
        .catch(() => undefined);
      // Signed out / another user meanwhile: the answer belongs to the previous session.
      if (!isCurrent(gen)) queryClient.removeQueries({ queryKey: songKey(id), exact: true });
    }
  })().finally(() => {
    if (refreshing?.p === p) refreshing = null;
  });
  refreshing = { gen, p };
  return p;
}

/* ─── Avatars (small; shown offline in the directory and on message boards) ─ */

let avatarRun: Promise<void> | null = null;
let avatarGen = 0;

export function syncAvatars(): Promise<void> {
  if (avatarRun && avatarGen === data.generation) return avatarRun;
  const gen = data.generation;
  avatarGen = gen;
  const run = (async () => {
    const members = queryClient.getQueryData<MemberDTO[]>(['members']);
    const content = await openContent();
    if (!members || !content) return;
    const wanted = new Set(members.filter((m) => m.avatarRev > 0).map((m) => avatarUrl(m.id, m.avatarRev, 128)));
    const stored = (await getMeta('avatars').catch(() => undefined)) ?? [];
    const stale = stored.filter((u) => !wanted.has(u));
    await deleteFromCache(content, stale);
    const kept = stored.filter((u) => wanted.has(u));
    if (!isCurrent(gen)) return;
    await setMeta('avatars', kept);
    for (const url of wanted) {
      if (!isCurrent(gen) || !navigator.onLine) break;
      if (kept.includes(url) && (await content.match(url))) continue;
      try {
        const res = await apiRaw(url.replace(/^\/api/, ''), { timeoutMs: 15_000 });
        if (validateAvatarResponse(res)) continue;
        if (!isCurrent(gen)) break;
        await content.put(url, res);
        if (!isCurrent(gen)) {
          await content.delete(url);
          break;
        }
        if (!kept.includes(url)) kept.push(url);
        await setMeta('avatars', kept);
      } catch {
        /* missing avatar (404) or offline: initials are shown instead */
      }
    }
  })().catch(() => undefined);
  const tracked: Promise<void> = run.finally(() => {
    if (avatarRun === tracked) avatarRun = null;
  });
  avatarRun = tracked;
  return tracked;
}

/* ─── Eviction (403/404 from media) ─────────────────────────────────────── */

/**
 * Removes URLs from both caches, the items table and the recent list. Packs that were complete and
 * lose a file show "Uppfærsla tiltæk" until it is downloaded again (or the manifest drops it).
 */
export async function evictUrls(urls: string[]): Promise<void> {
  if (urls.length === 0) return;
  const gen = data.generation;
  const set = new Set(urls);
  // Memory first: nothing serves or counts these copies from now on.
  const touched: PackRow[] = [];
  for (const u of urls) {
    const it = data.items.get(u);
    if (!it) continue;
    data.items.delete(u);
    for (const id of it.packIds) {
      const p = data.packs.get(id);
      if (p?.status === 'complete') {
        const next = { ...p, status: 'update' as const };
        data.packs.set(id, next);
        touched.push(next);
      }
    }
  }
  publish();
  const [content, recentCache] = await Promise.all([openContent(), openRecent()]);
  await deleteFromCache(content, urls);
  await withRecent(async (list) => {
    await deleteFromCache(recentCache, urls);
    const next = dropRecentUrls(list, set);
    return next.length !== list.length || next.some((e, i) => e.urls.length !== list[i]!.urls.length) ? next : null;
  });
  if (!isCurrent(gen)) return;
  try {
    const db = offlineDb();
    await db.transaction('rw', db.items, db.packs, async () => {
      await db.items.bulkDelete(urls);
      await db.packs.bulkPut(touched.map((p) => data.packs.get(p.id)).filter((p): p is PackRow => !!p));
    });
  } catch {
    /* the in-memory state is already corrected; the next manifest sync re-checks everything */
  }
}

/**
 * The media route refused a file (viewer request). 403 (not visible): every stored part of it goes.
 * 404 (that revision is not served — e.g. a song kept open while a new revision arrived): only that
 * revision and older ones, never a newer copy a pack already holds. Then the manifest is fetched
 * again so whatever is still visible is downloaded again.
 */
export async function evictRefusedFile(fileId: string, status: 403 | 404, rev: number): Promise<void> {
  const urls = staleFileUrls(fileId, status === 403 ? null : rev, data.items.values(), data.recent);
  const manifestStale = !!data.manifest?.files.some((f) => f.id === fileId && (status === 403 || f.rev <= rev));
  await evictUrls(urls);
  if ((urls.length > 0 || manifestStale) && navigator.onLine) void syncManifest();
}

setDownloaderHooks({
  resync: async () => {
    await syncManifest();
  },
  evict: evictUrls,
});

/* ─── Packs ─────────────────────────────────────────────────────────────── */

const MANIFEST_FRESH_MS = 5 * 60_000;

/** A manifest fresh enough to plan a new pack (fetches one when needed). */
async function freshManifest(): Promise<ManifestDTO> {
  await loading;
  if (!data.manifest || Date.now() - lastSyncOkAt > MANIFEST_FRESH_MS) {
    const ok = await syncManifest();
    if (!ok) throw new SavePackError('offline');
  }
  if (!data.manifest) throw new SavePackError('offline');
  return data.manifest;
}

/** Size estimate for the save sheet (null when the target is not visible / no manifest yet). */
export function previewPack(kind: PackKind, refId: string | null, allSections: boolean): { bytes: number; files: number; songs: number } | null {
  if (!data.manifest || !data.user) return null;
  const spec = { kind, refId, allSections };
  const plan = planPack(spec, data.manifest, data.user, data.tier);
  if (!plan) return null;
  return { bytes: plannedBytes(plan), files: plan.length, songs: packSongIds(spec, data.manifest)?.length ?? 0 };
}

/** Asks the browser not to evict our storage (first pack). */
async function requestPersist(): Promise<void> {
  try {
    const s = navigator.storage;
    if (s?.persist && !(await s.persisted?.())) await s.persist();
  } catch {
    /* not supported */
  }
}

async function linkItems(gen: number, pack: PackRow): Promise<void> {
  const plan = currentPlans().get(pack.id) ?? [];
  const planned = new Set(plan.map((p) => p.url));
  const db = offlineDb();
  const updates: ItemRow[] = [];
  const unlinked: string[] = [];
  for (const it of data.items.values()) {
    const has = it.packIds.includes(pack.id);
    if (planned.has(it.url) && !has) updates.push({ ...it, packIds: [...it.packIds, pack.id] });
    else if (!planned.has(it.url) && has) {
      const ids = it.packIds.filter((x) => x !== pack.id);
      if (ids.length > 0) updates.push({ ...it, packIds: ids });
      else unlinked.push(it.url);
    }
  }
  await deleteFromCache(await openContent(), unlinked);
  if (!isCurrent(gen)) return;
  await db.transaction('rw', db.items, async () => {
    await db.items.bulkPut(updates);
    await db.items.bulkDelete(unlinked);
  });
  if (!isCurrent(gen)) return;
  for (const it of updates) data.items.set(it.url, it);
  for (const u of unlinked) data.items.delete(u);
}

/** "Vista fyrir ónettengt": creates (or updates) a pack and starts downloading it. */
export async function savePack(input: { kind: PackKind; refId: string | null; title: string; allSections?: boolean }): Promise<void> {
  if (!data.user) {
    // State was reset (e.g. a wipe on user switch) while the app kept running: bind the signed-in user again.
    const me = queryClient.getQueryData<MeDTO | null>(['me']);
    if (me) await bindUser(me);
  }
  const gen = data.generation;
  const user = data.user;
  if (!user) throw new SavePackError('storage');
  const manifest = await freshManifest();
  const allSections = roleAtLeast(user.role, 'editor') ? !!input.allSections : false;
  const id = packId(input.kind, input.refId);
  const plan = planPack({ kind: input.kind, refId: input.refId, allSections }, manifest, user, data.tier);
  if (!plan) throw new SavePackError('gone');
  const first = data.packs.size === 0;
  const prev = data.packs.get(id);
  const row: PackRow = {
    id,
    kind: input.kind,
    refId: input.refId,
    title: input.title || prev?.title || '',
    createdAt: prev?.createdAt ?? Date.now(),
    status: 'pending',
    bytes: plannedBytes(plan),
    error: null,
    allSections,
  };
  try {
    await offlineOp((db) => db.packs.put(row));
  } catch {
    throw new SavePackError('storage');
  }
  if (!isCurrent(gen)) return;
  data.packs.set(id, row);
  await linkItems(gen, row);
  if (!isCurrent(gen)) return;
  if (missingItems(id).length === 0) {
    const done = { ...row, status: 'complete' as const };
    data.packs.set(id, done);
    await offlineOp((db) => db.packs.put(done));
  }
  publish();
  if (first) void requestPersist();
  void refreshSongDetails(packSongIds(row, manifest) ?? []);
  void runQueue();
}

/** "Fjarlægja": removes a pack; files no other pack needs are deleted from the device. */
export async function removePack(id: string): Promise<void> {
  const gen = data.generation;
  const pack = data.packs.get(id);
  if (!pack) return;
  // Memory first, synchronously: the queue (woken by the abort below) must not find this pack again,
  // and a late status write of the aborted file finds no pack to write to.
  data.packs.delete(id);
  const updates: ItemRow[] = [];
  const unlinked: string[] = [];
  for (const it of data.items.values()) {
    if (!it.packIds.includes(id)) continue;
    const ids = it.packIds.filter((x) => x !== id);
    if (ids.length > 0) updates.push({ ...it, packIds: ids });
    else unlinked.push(it.url);
  }
  for (const it of updates) data.items.set(it.url, it);
  for (const u of unlinked) data.items.delete(u);
  publish();
  abortUnwantedDownload();

  // Bookkeeping before the copies: if this fails the pack comes back intact; copies left behind by
  // an interrupted delete are strays the next manifest sync sweeps away.
  const db = offlineDb();
  await db.transaction('rw', db.items, db.packs, async () => {
    await db.packs.delete(id);
    await db.items.bulkPut(updates);
    await db.items.bulkDelete(unlinked);
  });
  if (!isCurrent(gen)) return;
  await deleteFromCache(await openContent(), unlinked);
  nudgeQueue();
}

/** Re-downloads every file of a pack (e.g. after storage trouble). */
export async function redownloadPack(id: string): Promise<void> {
  const gen = data.generation;
  if (!data.packs.has(id)) return;
  const urls = [...data.items.values()].filter((it) => it.packIds.includes(id)).map((it) => it.url);
  // The old copies go first while the items table still lists them, so the queue cannot start
  // storing a fresh copy that this delete would then remove.
  await deleteFromCache(await openContent(), urls);
  if (!isCurrent(gen) || !data.packs.has(id)) return;
  // Memory next (the queue sees the files as missing); other packs sharing them download them again too.
  const since = Date.now();
  const touched: string[] = [];
  const plans = currentPlans();
  for (const p of data.packs.values()) {
    if (p.id === id || urls.some((u) => plans.get(p.id)?.some((x) => x.url === u))) touched.push(p.id);
  }
  for (const u of urls) data.items.delete(u);
  for (const pid of touched) {
    const p = data.packs.get(pid);
    if (p) data.packs.set(pid, { ...p, status: 'pending', error: null });
  }
  publish();
  // Nothing in flight belongs to the deleted copies (only missing files are downloaded): no abort.
  void runQueue();
  const db = offlineDb();
  await db.transaction('rw', db.items, db.packs, async () => {
    // Rows the queue stored again meanwhile are newer than `since` and stay.
    await db.items
      .where('url')
      .anyOf(urls)
      .and((r) => r.cachedAt < since)
      .delete();
    await db.packs.bulkPut(touched.map((pid) => data.packs.get(pid)).filter((p): p is PackRow => !!p));
  });
}

/**
 * Retries packs that stopped with an error (all, or one). Without an id (every online start) a pack
 * that stopped because storage was full waits until the device has room for its next file plus the
 * reserve — otherwise each start would fill the freed space again. A retry of one pack (the user
 * asked) always runs.
 */
export async function retryPacks(id?: string): Promise<void> {
  const gen = data.generation;
  const ids: string[] = [];
  for (const p of [...data.packs.values()]) {
    if (p.status !== 'error' || (id && p.id !== id)) continue;
    if (!id && p.error === 'storage_full') {
      const next = missingItems(p.id)[0];
      if (next && (await roomFor(next.size)) === false) continue;
    }
    ids.push(p.id);
  }
  if (!isCurrent(gen)) return;
  // From the current rows (a pack removed or changed while the space was checked is left alone).
  const rows = ids
    .map((pid) => data.packs.get(pid))
    .filter((p): p is PackRow => p?.status === 'error')
    .map((p) => ({ ...p, status: 'pending' as const, error: null }));
  if (rows.length > 0) {
    await offlineOp((db) => db.packs.bulkPut(rows));
    if (!isCurrent(gen)) return;
    for (const r of rows) data.packs.set(r.id, r);
    publish();
  }
  nudgeQueue();
  void runQueue();
}

/** "Hreinsa": empties the recent-songs cache (listed copies and strays alike). */
export async function clearRecent(): Promise<void> {
  const recentCache = await openRecent();
  await withRecent(async (list) => {
    await deleteFromCache(
      recentCache,
      list.flatMap((e) => e.urls.map((u) => u.url)),
    );
    if (recentCache) await deleteFromCache(recentCache, (await recentCache.keys()).map((r) => r.url));
    return [];
  });
}

/** Coming back online / app start: retry failed packs and continue the queue. */
export function resumeDownloads(): void {
  if (!navigator.onLine) return;
  void retryPacks();
}

export function manifestAge(): number {
  return lastSyncOkAt ? Date.now() - lastSyncOkAt : Infinity;
}
